import React, { useState, useContext, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Divider from "@amzn/meridian/divider";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Loader from "@amzn/meridian/loader";
import { AlertType } from "@amzn/meridian/alert/alert";
import AdminContext from "../../../store/admin-context";
import AppConfigContext, { checkFeatureEnabled } from "../../../store/app-config";
import ContentLayout from "../../layout/ContentLayout";
import AdminApiFactory from "../../../admin-api/AdminApiFactory";
import { FEATURE_COHORT_SCAN_WINDOW, parseBusinessSpace } from "../../../constants";
import { BusinessSpace } from "../../../admin-api/generated-src";
import SelectBusinessSpacePrompt from "../../alert/SelectBusinessSpacePrompt";

const BUSINESS_SPACE_DEFAULTS: Record<BusinessSpace, { beforeStartMinutes: number; afterEndMinutes: number }> = {
    [BusinessSpace.SortCenter]: { beforeStartMinutes: 10, afterEndMinutes: 60 },
    [BusinessSpace.Air]: { beforeStartMinutes: 60, afterEndMinutes: 960 },
    [BusinessSpace.Amxl]: { beforeStartMinutes: 60, afterEndMinutes: 960 },
    [BusinessSpace.Tom]: { beforeStartMinutes: 5, afterEndMinutes: 5 },
    // XRSC+ uses the base/sort-center default scan window.
    [BusinessSpace.Xrsc]: { beforeStartMinutes: 10, afterEndMinutes: 60 },
    // AMXL expansion business spaces use the AMXL default scan window.
    [BusinessSpace.AmxlFc]: { beforeStartMinutes: 60, afterEndMinutes: 960 },
    [BusinessSpace.AmxlFusion]: { beforeStartMinutes: 60, afterEndMinutes: 960 },
    [BusinessSpace.AmxlIxd]: { beforeStartMinutes: 60, afterEndMinutes: 960 },
};

function getDefaultsForBusinessSpace(businessSpace: BusinessSpace | "") {
    return (businessSpace && BUSINESS_SPACE_DEFAULTS[businessSpace]) || BUSINESS_SPACE_DEFAULTS[BusinessSpace.SortCenter];
}

export default function ScanWindowConfigPage() {
    const history = useHistory();
    const adminContext = useContext(AdminContext);
    const appConfigContext = useContext(AppConfigContext);
    const { config, region, stage } = appConfigContext;
    const { selectedNode, changeSelectedNode } = adminContext;
    const { businessSpace: businessSpaceParam, nodeId } = useParams<{ businessSpace: string; nodeId: string }>();
    const businessSpace = parseBusinessSpace(businessSpaceParam);
    const AdminApi = AdminApiFactory();

    const defaults = getDefaultsForBusinessSpace(businessSpace);
    const [windowMode, setWindowMode] = useState("SHIFT");
    const [beforeStartMinutes, setBeforeStartMinutes] = useState(String(defaults.beforeStartMinutes));
    const [afterEndMinutes, setAfterEndMinutes] = useState(String(defaults.afterEndMinutes));
    const [lookaheadMinutes, setLookaheadMinutes] = useState("0");
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: AlertType; text: string } | null>(null);
    const [updatedBy, setUpdatedBy] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [isExplicitConfig, setIsExplicitConfig] = useState(false);
    const [samiEnabled, setSamiEnabled] = useState(false);
    const [needsShiftResave, setNeedsShiftResave] = useState(false);

    useEffect(() => {
        if (nodeId !== selectedNode) {
            changeSelectedNode(nodeId);
        }
        if (config !== null) {
            if (!checkFeatureEnabled(config, FEATURE_COHORT_SCAN_WINDOW, region, stage, nodeId, businessSpace)) {
                history.push("/");
            }
        }
    }, [nodeId, selectedNode, config, businessSpace]);

    useEffect(() => {
        if (!businessSpace) {
            setLoading(false);
            return;
        }
        loadConfig();
    }, [businessSpace, nodeId]);

    async function loadConfig() {
        if (!businessSpace) {
            return;
        }
        try {
            const res = await AdminApi.getScanWindowConfig(businessSpace, nodeId);
            const data = res.data as (typeof res.data & { samiEnabled?: boolean });
            const enabled = data.samiEnabled === true;
            setSamiEnabled(enabled);
            // When SAMI (segment scheduling) is off, only Shift-level applies — force the mode to SHIFT
            // regardless of any previously stored COHORT value (the user is prompted to re-save).
            setWindowMode(enabled ? data.windowMode : "SHIFT");
            // Only nudge a re-save when there's actually a stale COHORT config to correct (SAMI off +
            // stored windowMode COHORT). A node already on SHIFT needs no prompt.
            setNeedsShiftResave(!enabled && data.windowMode === "COHORT");
            setBeforeStartMinutes(String(data.beforeStartMinutes));
            setAfterEndMinutes(String(data.afterEndMinutes));
            setLookaheadMinutes(String(data.lookaheadMinutes));
            setUpdatedBy(data.updatedBy || null);
            setUpdatedAt(data.updatedAt || null);
            setIsExplicitConfig(!!data.updatedBy);
        } catch (ex) {
            if (ex.response?.status === 404) {
                setBeforeStartMinutes(String(defaults.beforeStartMinutes));
                setAfterEndMinutes(String(defaults.afterEndMinutes));
                setIsExplicitConfig(false);
                setNeedsShiftResave(false);
            } else {
                console.error(`Error GET /admin/config/${businessSpace}/${nodeId}/scan-window`, ex);
                setMessage({ type: "error", text: "Failed to load configuration" });
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleSave() {
        if (!businessSpace) {
            return;
        }
        setMessage(null);
        try {
            await AdminApi.putScanWindowConfig(businessSpace, nodeId, {
                windowMode: windowMode as any,
                beforeStartMinutes: Number(beforeStartMinutes),
                afterEndMinutes: Number(afterEndMinutes),
                lookaheadMinutes: Number(lookaheadMinutes),
            });
            setMessage({ type: "success", text: "Configuration saved successfully. Changes may take up to 5 minutes to take effect at kiosks." });
            await loadConfig();
        } catch (ex) {
            console.error(`Error PUT /admin/config/${businessSpace}/${nodeId}/scan-window`, ex);
            const msg = (typeof ex.response?.data === "string"
                ? ex.response.data
                : ex.response?.data?.message) || "Failed to save configuration";
            setMessage({ type: "error", text: msg });
        }
    }

    if (!businessSpace) {
        return (
            <ContentLayout nodeId={nodeId} helperText="" subTitle="Scan Window Configuration">
                <SelectBusinessSpacePrompt />
            </ContentLayout>
        );
    }

    if (loading) {
        return (
            <ContentLayout nodeId={nodeId} helperText="" subTitle="Scan Window Configuration">
                <Loader />
            </ContentLayout>
        );
    }

    const isCohort = windowMode === "COHORT";
    const beforeLabel = isCohort ? "Before Segment Start (minutes)" : "Before Shift Start (minutes)";
    const afterLabel = isCohort ? "After Segment Start (minutes)" : "After Shift Start (minutes)";

    return (
        <ContentLayout nodeId={nodeId} helperText="" subTitle="Scan Window Configuration">
            <Column spacing="400">
                <Alert type="informational">
                    Scan window changes may take up to 5 minutes to take effect at kiosks.
                </Alert>
                <Box backgroundColor="secondary" spacingInset="400" width="100%">
                    <Column spacing="300">
                        <Column spacing="200">
                            <Text type="h100">Window Mode</Text>
                            <Text type="b200">• SHIFT — Scan window applies to the entire shift duration.</Text>
                            <Text type="b200">• COHORT — Scan window applies per-segment. Each associate&apos;s individual assignment has its own window.</Text>
                        </Column>
                        <Column spacing="200">
                            <Text type="h100">{isCohort ? "Before Segment Start" : "Before Shift Start"}</Text>
                            <Text type="b200">How many minutes before the {isCohort ? "segment" : "shift"} start time the kiosk will accept a badge scan.</Text>
                        </Column>
                        <Column spacing="200">
                            <Text type="h100">{isCohort ? "After Segment Start" : "After Shift Start"}</Text>
                            <Text type="b200">How many minutes after the {isCohort ? "segment" : "shift"} start time the kiosk will still accept a badge scan.</Text>
                        </Column>
                        {isCohort && (
                            <Column spacing="200">
                                <Text type="h100">Lookahead</Text>
                                <Text type="b200">If the associate&apos;s next segment starts within this many minutes, the kiosk will show that next assignment instead of the current one. Useful during shift transitions.</Text>
                            </Column>
                        )}
                    </Column>
                </Box>
                <Divider />
                {message && <Alert type={message.type}>{message.text}</Alert>}
                {needsShiftResave && (
                    <Alert type="warning">
                        Segment scheduling (SAMI) is not enabled for this node, so only Shift-level scan
                        windows apply. Please re-save this configuration with Shift level.
                    </Alert>
                )}
                <Row>
                    <Box width={250}><Text>Window Mode</Text></Box>
                    <Box>
                        <Select value={windowMode} onChange={setWindowMode} width={300}>
                            <SelectOption value="SHIFT" label="Shift Level" />
                            {samiEnabled && <SelectOption value="COHORT" label="Cohort Level" />}
                        </Select>
                    </Box>
                </Row>
                <Row>
                    <Box width={250}><Text>{beforeLabel}</Text></Box>
                    <Box>
                        <Input type="number" value={beforeStartMinutes} onChange={setBeforeStartMinutes} width={300} placeholder={String(defaults.beforeStartMinutes)} />
                    </Box>
                </Row>
                <Row>
                    <Box width={250}><Text>{afterLabel}</Text></Box>
                    <Box>
                        <Input type="number" value={afterEndMinutes} onChange={setAfterEndMinutes} width={300} placeholder={String(defaults.afterEndMinutes)} />
                    </Box>
                </Row>
                {isCohort && (
                    <Row>
                        <Box width={250}><Text>Lookahead (minutes)</Text></Box>
                        <Box>
                            <Input type="number" value={lookaheadMinutes} onChange={setLookaheadMinutes} width={300} />
                        </Box>
                    </Row>
                )}
                <Row>
                    <Button type="primary" onClick={handleSave}>Save</Button>
                </Row>
                {updatedBy && updatedAt && (
                    <Text type="b200" color="secondary">
                        Last updated by {updatedBy} on {new Date(updatedAt).toLocaleString()}
                    </Text>
                )}
                {!isExplicitConfig && (
                    <Alert type="warning">
                        No configuration saved — using system defaults for {businessSpace} ({defaults.beforeStartMinutes} min before start, {defaults.afterEndMinutes} min after end).
                    </Alert>
                )}
            </Column>
        </ContentLayout>
    );
}
